import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "@/lib/toast";
import { PhotoGallery } from "@/components/PhotoUploader";
import { Trash2, Edit, X, UserMinus, Printer, ShieldCheck, Briefcase, Award, Calendar as CalIcon } from "lucide-react";

export function AdminEmployees() {
  const [employees, setEmployees] = useState([]);
  const [clients, setClients] = useState([]);
  const [roles, setRoles] = useState([]);
  const [assigning, setAssigning] = useState(null);
  const [hrEditing, setHrEditing] = useState(null);

  const load = () => {
    api.get("/employees").then((r) => setEmployees(r.data));
    api.get("/clients").then((r) => setClients(r.data));
    api.get("/admin/roles").then((r) => setRoles(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const changeRole = async (userId, roleId) => {
    try {
      await api.post(`/admin/users/${userId}/role`, { role_id: roleId });
      toast.success("Role assigned");
      setAssigning(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Something went wrong — please try again."); }
  };

  const promote = async (uid) => {
    if (!confirm("Promote to employee?")) return;
    await api.post(`/users/${uid}/role`, { role: "employee" });
    load();
    toast.success("Role updated");
  };

  const demote = async (e) => {
    if (!confirm(`Demote ${e.name} back to client?`)) return;
    await api.post(`/users/${e.user_id}/role`, { role: "client" });
    load();
    toast.success("Demoted to client");
  };

  const remove = async (e) => {
    if (!confirm(`Permanently delete ${e.name}? This cannot be undone.`)) return;
    try {
      await api.delete(`/users/${e.user_id}`);
      toast.success("Employee deleted");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div data-testid="admin-employees">
      <p className="overline">Team</p>
      <h1 className="text-3xl font-heading font-medium text-[#0A2540] dark:text-white mt-2 mb-8">Employees</h1>
      <div className="card-pb overflow-hidden mb-8">
        <div className="px-5 py-3 bg-slate-50 dark:bg-slate-800 border-b border-[#E2E8F0] dark:border-slate-700 font-medium text-[#0A2540] dark:text-white">
          Current employees ({employees.length})
        </div>
        <ul>
          {employees.map((e) => (
            <li key={e.user_id} className="px-5 py-3 border-b border-[#E2E8F0] dark:border-slate-700 last:border-0 flex justify-between items-center" data-testid={`emp-${e.user_id}`}>
              <div>
                <div className="font-medium text-[#0A2540] dark:text-white">{e.name}</div>
                <div className="text-xs text-[#475569] dark:text-slate-400">
                  {e.employee_code && <span className="font-mono text-[10px] mr-1.5 text-[#C9A356]">{e.employee_code}</span>}
                  {e.email}{e.phone ? ` · ${e.phone}` : ""}
                </div>
                {e.role_id && (
                  <div className="text-[10px] mt-1 inline-flex items-center gap-1 text-[#C9A356]">
                    <ShieldCheck className="w-3 h-3" /> {roles.find((r) => r.role_id === e.role_id)?.name || e.role_id}
                  </div>
                )}
              </div>
              <div className="flex gap-2 items-center">
                <button onClick={() => setHrEditing(e)} data-testid={`hr-${e.user_id}`}
                        className="text-xs text-[#0A2540] dark:text-white hover:text-[#C9A356] px-2 py-1 inline-flex items-center gap-1">
                  <Briefcase className="w-3.5 h-3.5" /> HR
                </button>
                <button onClick={() => setAssigning(e)} data-testid={`assign-role-${e.user_id}`}
                        className="text-xs text-[#C9A356] hover:text-[#8A6B14] px-2 py-1 inline-flex items-center gap-1">
                  <ShieldCheck className="w-3.5 h-3.5" /> Role
                </button>
                <button onClick={() => demote(e)} data-testid={`demote-${e.user_id}`}
                        className="text-xs text-[#475569] hover:text-[#0A2540] dark:hover:text-white px-2 py-1 inline-flex items-center gap-1">
                  <UserMinus className="w-3.5 h-3.5" /> Demote
                </button>
                <button onClick={() => remove(e)} data-testid={`del-emp-${e.user_id}`}
                        className="text-xs text-red-600 hover:text-red-700 px-2 py-1 inline-flex items-center gap-1">
                  <Trash2 className="w-3.5 h-3.5" /> Delete
                </button>
              </div>
            </li>
          ))}
          {employees.length === 0 && <li className="px-5 py-6 text-sm text-[#475569] dark:text-slate-400">No employees yet. Promote a client below.</li>}
        </ul>
      </div>

      <div className="card-pb overflow-hidden">
        <div className="px-5 py-3 bg-slate-50 dark:bg-slate-800 border-b border-[#E2E8F0] dark:border-slate-700 font-medium text-[#0A2540] dark:text-white">
          Promote a client to employee
        </div>
        <ul>
          {clients.slice(0, 20).map((c) => (
            <li key={c.user_id} className="px-5 py-3 border-b border-[#E2E8F0] dark:border-slate-700 last:border-0 flex justify-between items-center">
              <div>
                <div className="font-medium text-[#0A2540] dark:text-white">{c.name}</div>
                <div className="text-xs text-[#475569] dark:text-slate-400">{c.email}</div>
              </div>
              <button onClick={() => promote(c.user_id)} className="btn-outline-navy text-xs py-1 px-3" data-testid={`promote-${c.user_id}`}>
                Promote
              </button>
            </li>
          ))}
        </ul>
      </div>
      {assigning && (
        <div className="fixed inset-0 z-50 bg-black/60 grid place-items-center px-4" onClick={() => setAssigning(null)} role="dialog" aria-modal="true" data-testid="role-modal">
          <div className="bg-white dark:bg-slate-900 rounded-xl max-w-sm w-full shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="p-4 border-b border-[#E2E8F0] dark:border-slate-700 flex items-center justify-between">
              <h3 className="font-heading text-lg text-[#0A2540] dark:text-white">Assign role to {assigning.name}</h3>
              <button onClick={() => setAssigning(null)} aria-label="Close"><X className="w-5 h-5 text-[#475569]" /></button>
            </div>
            <div className="p-4 max-h-[60vh] overflow-y-auto space-y-1.5">
              {roles.map((r) => (
                <button
                  key={r.role_id}
                  onClick={() => changeRole(assigning.user_id, r.role_id)}
                  data-testid={`role-option-${r.role_id}`}
                  className={`w-full text-left px-3 py-2 rounded border transition ${
                    assigning.role_id === r.role_id
                      ? "border-[#C9A356] bg-[#FFFBF0] dark:bg-amber-900/10"
                      : "border-[#E2E8F0] dark:border-slate-700 hover:border-[#C9A356]"
                  }`}
                >
                  <div className="text-sm font-medium text-[#0A2540] dark:text-white">{r.name}</div>
                  <div className="text-[11px] text-[#475569] dark:text-slate-400 truncate">{r.description}</div>
                </button>
              ))}
              {roles.length === 0 && <div className="text-sm text-[#475569] text-center py-6">No roles yet.</div>}
            </div>
          </div>
        </div>
      )}
      {hrEditing && (
        <HREditor employee={hrEditing} onClose={() => { setHrEditing(null); load(); }} />
      )}
    </div>
  );
}

function HREditor({ employee, onClose }) {
  const [hr, setHr] = useState({});
  const [certs, setCerts] = useState([]);
  const [balance, setBalance] = useState({});
  const [leaves, setLeaves] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showCertForm, setShowCertForm] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [detail, leaveList] = await Promise.all([
        api.get(`/admin/employees/${employee.user_id}/hr`),
        api.get(`/leave-requests`).catch(() => ({ data: [] })),
      ]);
      setHr(detail.data.hr || {});
      setCerts(detail.data.certifications || []);
      setBalance(detail.data.leave_balance || {});
      setLeaves((leaveList.data || []).filter((l) => l.user_id === employee.user_id));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Load failed");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/employees/${employee.user_id}/hr`, hr);
      toast.success("HR saved");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const addCert = async (cert) => {
    try {
      await api.post(`/admin/employees/${employee.user_id}/certifications`, cert);
      toast.success("Certification added");
      setShowCertForm(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Something went wrong — please try again."); }
  };

  const delCert = async (cid) => {
    if (!confirm("Delete this certification?")) return;
    await api.delete(`/admin/employees/${employee.user_id}/certifications/${cid}`);
    load();
  };

  const saveBalance = async () => {
    try {
      await api.patch(`/admin/employees/${employee.user_id}/leave-balance`, balance);
      toast.success("Leave balance saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Something went wrong — please try again."); }
  };

  const decide = async (leaveId, status) => {
    try {
      await api.patch(`/leave-requests/${leaveId}`, { status });
      toast.success(status === "approved" ? "Approved" : "Denied");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Something went wrong — please try again."); }
  };

  const set = (k, v) => setHr({ ...hr, [k]: v });
  const inputCls = "w-full border border-[#E2E8F0] dark:border-slate-700 dark:bg-slate-800 dark:text-white rounded-md px-3 py-2 text-sm";

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4" onClick={onClose} role="dialog" aria-modal="true">
      <div className="bg-white dark:bg-slate-900 rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="hr-editor">
        <div className="sticky top-0 bg-white dark:bg-slate-900 p-5 border-b border-[#E2E8F0] dark:border-slate-700 flex items-center justify-between">
          <div>
            <p className="overline">{employee.employee_code || employee.user_id}</p>
            <h3 className="font-heading text-xl text-[#0A2540] dark:text-white mt-1">{employee.name}</h3>
          </div>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>

        {loading ? (
          <div className="p-6 text-sm text-[#94A3B8]">Loading…</div>
        ) : (
        <div className="p-5 space-y-6">
          {/* HR fields */}
          <section>
            <h4 className="text-sm font-bold text-[#0A2540] dark:text-white mb-3 inline-flex items-center gap-2"><Briefcase className="w-4 h-4 text-[#C9A356]" /> Employment</h4>
            <div className="grid grid-cols-2 gap-3">
              <FieldWrap label="Job title"><input value={hr.title || ""} onChange={(e) => set("title", e.target.value)} data-testid="hr-title" className={inputCls} /></FieldWrap>
              <FieldWrap label="Department"><input value={hr.department || ""} onChange={(e) => set("department", e.target.value)} data-testid="hr-dept" className={inputCls} /></FieldWrap>
              <FieldWrap label="Employment type">
                <select value={hr.employment_type || ""} onChange={(e) => set("employment_type", e.target.value)} data-testid="hr-etype" className={inputCls}>
                  <option value="">—</option>
                  <option value="full_time">Full-time</option>
                  <option value="part_time">Part-time</option>
                  <option value="contractor">Contractor</option>
                  <option value="intern">Intern</option>
                </select>
              </FieldWrap>
              <FieldWrap label="Hire date"><input type="date" value={hr.hire_date || ""} onChange={(e) => set("hire_date", e.target.value)} data-testid="hr-hire" className={inputCls} /></FieldWrap>
              <FieldWrap label="Salary type">
                <select value={hr.salary_type || ""} onChange={(e) => set("salary_type", e.target.value)} data-testid="hr-stype" className={inputCls}>
                  <option value="">—</option>
                  <option value="monthly">Monthly</option>
                  <option value="hourly">Hourly</option>
                  <option value="project">Project</option>
                </select>
              </FieldWrap>
              <FieldWrap label={hr.salary_type === "hourly" ? "Hourly rate ($)" : "Salary amount ($)"}>
                <input type="number" step="0.01" value={hr.salary_type === "hourly" ? (hr.hourly_rate ?? "") : (hr.salary_amount ?? "")}
                       onChange={(e) => set(hr.salary_type === "hourly" ? "hourly_rate" : "salary_amount", e.target.value)}
                       data-testid="hr-salary" className={inputCls} />
              </FieldWrap>
              <FieldWrap label="Tax ID / SSN"><input value={hr.tax_id || ""} onChange={(e) => set("tax_id", e.target.value)} data-testid="hr-tax" className={inputCls} /></FieldWrap>
              <FieldWrap label="Bank (last 4)"><input maxLength={4} value={hr.bank_last4 || ""} onChange={(e) => set("bank_last4", e.target.value)} data-testid="hr-bank" className={inputCls} /></FieldWrap>
            </div>
            <div className="mt-3 flex justify-end">
              <button onClick={save} disabled={saving} className="btn-accent text-sm" data-testid="save-hr">
                {saving ? "Saving…" : "Save HR"}
              </button>
            </div>
          </section>

          {/* Certifications */}
          <section>
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-bold text-[#0A2540] dark:text-white inline-flex items-center gap-2"><Award className="w-4 h-4 text-[#C9A356]" /> Certifications</h4>
              <button onClick={() => setShowCertForm(true)} className="text-xs text-[#C9A356] hover:underline" data-testid="add-cert-btn">+ Add</button>
            </div>
            {certs.length === 0 ? (
              <p className="text-xs text-[#94A3B8] italic">No certifications on file.</p>
            ) : (
              <ul className="space-y-2">
                {certs.map((c) => (
                  <li key={c.cert_id} className="flex items-center justify-between p-2 border border-[#E2E8F0] dark:border-slate-700 rounded" data-testid={`cert-${c.cert_id}`}>
                    <div>
                      <div className="text-sm text-[#0A2540] dark:text-white">{c.name}</div>
                      <div className="text-[11px] text-[#94A3B8]">
                        {c.issuer && `${c.issuer} · `}
                        {c.issued_at && `issued ${c.issued_at}`}
                        {c.expires_at && ` · expires ${c.expires_at}`}
                      </div>
                    </div>
                    <button onClick={() => delCert(c.cert_id)} className="text-red-500 hover:text-red-700"><Trash2 className="w-4 h-4" /></button>
                  </li>
                ))}
              </ul>
            )}
            {showCertForm && <CertForm onCancel={() => setShowCertForm(false)} onSave={addCert} />}
          </section>

          {/* Leave balance */}
          <section>
            <h4 className="text-sm font-bold text-[#0A2540] dark:text-white mb-3 inline-flex items-center gap-2"><CalIcon className="w-4 h-4 text-[#C9A356]" /> Leave balance (days)</h4>
            <div className="grid grid-cols-4 gap-3">
              <FieldWrap label="PTO"><input type="number" value={balance.pto_days ?? 0} onChange={(e) => setBalance({ ...balance, pto_days: e.target.value })} className={inputCls} data-testid="bal-pto" /></FieldWrap>
              <FieldWrap label="Sick"><input type="number" value={balance.sick_days ?? 0} onChange={(e) => setBalance({ ...balance, sick_days: e.target.value })} className={inputCls} data-testid="bal-sick" /></FieldWrap>
              <FieldWrap label="Used PTO"><input type="number" value={balance.used_pto ?? 0} onChange={(e) => setBalance({ ...balance, used_pto: e.target.value })} className={inputCls} /></FieldWrap>
              <FieldWrap label="Used sick"><input type="number" value={balance.used_sick ?? 0} onChange={(e) => setBalance({ ...balance, used_sick: e.target.value })} className={inputCls} /></FieldWrap>
            </div>
            <div className="mt-3 flex justify-end">
              <button onClick={saveBalance} className="btn-outline-navy text-xs py-1.5 px-3" data-testid="save-balance">Save balance</button>
            </div>
          </section>

          {/* Leave requests */}
          <section>
            <h4 className="text-sm font-bold text-[#0A2540] dark:text-white mb-3">Leave requests</h4>
            {leaves.length === 0 ? (
              <p className="text-xs text-[#94A3B8] italic">No leave requests.</p>
            ) : (
              <ul className="space-y-2">
                {leaves.map((l) => (
                  <li key={l.leave_id} className="p-3 border border-[#E2E8F0] dark:border-slate-700 rounded flex items-center justify-between" data-testid={`leave-${l.leave_id}`}>
                    <div>
                      <div className="text-sm text-[#0A2540] dark:text-white capitalize">
                        {l.leave_type} · {l.days} day{l.days > 1 ? "s" : ""}
                        <span className="ml-2 text-[10px] font-mono text-[#94A3B8]">{l.leave_number}</span>
                      </div>
                      <div className="text-[11px] text-[#475569] dark:text-slate-400">{l.start_date} → {l.end_date} · {l.reason || "no reason"}</div>
                    </div>
                    {l.status === "pending" ? (
                      <div className="flex gap-1">
                        <button onClick={() => decide(l.leave_id, "approved")} className="text-xs text-emerald-600 hover:underline" data-testid={`approve-leave-${l.leave_id}`}>Approve</button>
                        <button onClick={() => decide(l.leave_id, "denied")} className="text-xs text-red-600 hover:underline">Deny</button>
                      </div>
                    ) : (
                      <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded ${l.status === "approved" ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>{l.status}</span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
        )}
      </div>
    </div>
  );
}

function FieldWrap({ label, children }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-wider text-[#475569] dark:text-slate-400 mb-1 font-bold">{label}</label>
      {children}
    </div>
  );
}

function CertForm({ onCancel, onSave }) {
  const [form, setForm] = useState({ name: "", issuer: "", issued_at: "", expires_at: "" });
  const cls = "w-full border border-[#E2E8F0] dark:border-slate-700 dark:bg-slate-800 dark:text-white rounded-md px-3 py-2 text-sm";
  return (
    <div className="mt-2 p-3 border-2 border-dashed border-[#C9A356] rounded bg-[#FFFBF0]/40 space-y-2">
      <input placeholder="Certification name (OSHA 30, NFPA, etc.)" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={cls} data-testid="cert-name" />
      <input placeholder="Issuer" value={form.issuer} onChange={(e) => setForm({ ...form, issuer: e.target.value })} className={cls} />
      <div className="grid grid-cols-2 gap-2">
        <input type="date" value={form.issued_at} onChange={(e) => setForm({ ...form, issued_at: e.target.value })} className={cls} />
        <input type="date" value={form.expires_at} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} className={cls} />
      </div>
      <div className="flex gap-2 justify-end">
        <button onClick={onCancel} className="text-xs text-[#475569] px-2 py-1">Cancel</button>
        <button onClick={() => form.name && onSave(form)} className="btn-accent text-xs py-1 px-2" data-testid="save-cert">Add</button>
      </div>
    </div>
  );
}

export function AdminWorkOrders() {
  const [workOrders, setWorkOrders] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [projects, setProjects] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ project_id: "", title: "", description: "", assigned_employee_id: "" });

  const load = async () => {
    const [wo, em, pr] = await Promise.all([
      api.get("/work-orders"),
      api.get("/employees"),
      api.get("/projects"),
    ]);
    setWorkOrders(wo.data);
    setEmployees(em.data);
    setProjects(pr.data);
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.project_id || !form.title) { toast.error("Project & title required"); return; }
    try {
      await api.post("/work-orders", form);
      toast.success("Work order created");
      setShowForm(false);
      setForm({ project_id: "", title: "", description: "", assigned_employee_id: "" });
      load();
    } catch {
      toast.error("Could not create the work order. Check the details and try again.");
    }
  };

  const reassign = async (wo, employee_id) => {
    await api.patch(`/work-orders/${wo.work_order_id}`, { assigned_employee_id: employee_id });
    load();
  };

  return (
    <div data-testid="admin-work-orders" className="printable-root">
      <div className="flex justify-between items-start mb-8 print:hidden">
        <div>
          <p className="overline">Field operations</p>
          <h1 className="text-3xl font-heading font-medium text-[#0A2540] dark:text-white mt-2">Work Orders</h1>
        </div>
        <div className="flex gap-2">
          <button onClick={() => window.print()} className="btn-outline-navy text-sm" data-testid="print-wo-btn">
            <Printer className="w-4 h-4 mr-1" /> Print / PDF
          </button>
          <button onClick={() => setShowForm(!showForm)} className="btn-accent" data-testid="new-wo-btn">+ Work Order</button>
        </div>
      </div>

      {/* Print-only header */}
      <div className="hidden print:block mb-6">
        <div className="flex items-center gap-3 mb-2">
          <img src="/logo.png" alt="" className="w-12 h-12 object-contain" />
          <div>
            <div className="font-bold text-lg">POWERBUILT SXM</div>
            <div className="text-xs text-slate-500">Solar · Electrical · Maintenance</div>
          </div>
        </div>
        <h1 className="text-2xl font-bold mt-3">Work Orders</h1>
        <div className="text-xs text-slate-500">Generated {new Date().toLocaleString()} · {workOrders.length} orders</div>
      </div>

      {showForm && (
        <div className="card-pb p-5 mb-6 bg-slate-50 dark:bg-slate-800" data-testid="wo-form">
          <div className="grid sm:grid-cols-2 gap-3">
            <select value={form.project_id} onChange={(e) => setForm({ ...form, project_id: e.target.value })} data-testid="wo-project" className="border border-[#E2E8F0] dark:border-slate-700 dark:bg-slate-900 dark:text-white rounded-md px-3 py-2 text-sm">
              <option value="">Select project</option>
              {projects.map((p) => <option key={p.project_id} value={p.project_id}>{p.customer_code} - {p.title}</option>)}
            </select>
            <select value={form.assigned_employee_id} onChange={(e) => setForm({ ...form, assigned_employee_id: e.target.value })} data-testid="wo-employee" className="border border-[#E2E8F0] dark:border-slate-700 dark:bg-slate-900 dark:text-white rounded-md px-3 py-2 text-sm">
              <option value="">Assign employee</option>
              {employees.map((e) => <option key={e.user_id} value={e.user_id}>{e.name}</option>)}
            </select>
            <input placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="wo-title" className="border border-[#E2E8F0] dark:border-slate-700 dark:bg-slate-900 dark:text-white rounded-md px-3 py-2 text-sm sm:col-span-2" />
            <textarea placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="wo-desc" rows="2" className="border border-[#E2E8F0] dark:border-slate-700 dark:bg-slate-900 dark:text-white rounded-md px-3 py-2 text-sm sm:col-span-2"></textarea>
          </div>
          <button onClick={create} className="btn-primary mt-4 text-sm" data-testid="wo-create">Create</button>
        </div>
      )}

      <div className="space-y-4">
        {workOrders.map((wo) => (
          <div key={wo.work_order_id} className="card-pb p-5" data-testid={`wo-${wo.work_order_id}`}>
            <div className="flex justify-between items-start mb-2">
              <div>
                <div className="font-heading font-medium text-[#0A2540] dark:text-white">{wo.title}</div>
                <div className="text-xs text-[#475569] dark:text-slate-400">{wo.customer_code} · #{wo.work_order_id}</div>
              </div>
              <div className="flex items-center gap-2">
                <span className={`text-xs uppercase font-bold tracking-wide px-2 py-0.5 rounded-full ${
                  wo.status === "completed" ? "bg-emerald-100 text-emerald-700" : wo.status === "in_progress" ? "bg-blue-100 text-blue-700" : "bg-slate-100 text-slate-700"
                }`}>{wo.status?.replace(/_/g, " ")}</span>
                <button onClick={() => setEditing(wo)} data-testid={`edit-wo-${wo.work_order_id}`}
                        className="p-1.5 rounded hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-500">
                  <Edit className="w-4 h-4" />
                </button>
              </div>
            </div>
            <p className="text-sm text-[#475569] dark:text-slate-300 mt-2">{wo.description}</p>
            <div className="mt-3 flex items-center gap-3 text-sm">
              <span className="text-[#475569] dark:text-slate-400">Assigned:</span>
              <select
                value={wo.assigned_employee_id || ""}
                onChange={(e) => reassign(wo, e.target.value)}
                data-testid={`wo-reassign-${wo.work_order_id}`}
                className="border border-[#E2E8F0] dark:border-slate-700 dark:bg-slate-800 dark:text-white rounded px-2 py-1 text-xs"
              >
                <option value="">Unassigned</option>
                {employees.map((e) => <option key={e.user_id} value={e.user_id}>{e.name}</option>)}
              </select>
            </div>
            {wo.logs?.length > 0 && (
              <div className="mt-4 border-t border-[#E2E8F0] dark:border-slate-700 pt-3">
                <p className="overline mb-2">Work logs</p>
                <ul className="space-y-2">
                  {wo.logs.map((l) => (
                    <li key={l.log_id} className="text-xs text-[#475569] dark:text-slate-400 border-l-2 border-[#C9A356] pl-2">
                      <span className="font-medium text-[#0A2540] dark:text-white">{l.employee_name}</span> · {l.hours}h · {new Date(l.timestamp).toLocaleString()}<br />
                      {l.text}
                      {l.photos?.length > 0 && <PhotoGallery photos={l.photos} />}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        ))}
        {workOrders.length === 0 && <p className="text-[#475569] dark:text-slate-400">No work orders yet</p>}
      </div>

      {editing && <EditWorkOrderModal wo={editing} projects={projects} employees={employees} onClose={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function EditWorkOrderModal({ wo, projects, employees, onClose }) {
  const [form, setForm] = useState({
    title: wo.title || "",
    description: wo.description || "",
    status: wo.status || "open",
    assigned_employee_id: wo.assigned_employee_id || "",
  });
  const [crewNotes, setCrewNotes] = useState(wo.crew_notes || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/work-orders/${wo.work_order_id}`, form);
      // Save crew notes separately (different endpoint)
      await api.patch(`/work-orders/${wo.work_order_id}/crew-notes`, { crew_notes: crewNotes });
      toast.success("Work order updated");
      onClose();
    } catch {
      toast.error("Update failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4" onClick={onClose}>
      <div className="bg-white dark:bg-slate-900 rounded-xl p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()} data-testid="edit-wo-modal">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-xl text-[#0A2540] dark:text-white">Edit work order</h3>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="block text-xs uppercase tracking-wider text-[#475569] dark:text-slate-400 mb-1 font-bold">Title</label>
            <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="edit-wo-title"
                   className="w-full border border-[#E2E8F0] dark:border-slate-700 dark:bg-slate-800 dark:text-white rounded-md px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider text-[#475569] dark:text-slate-400 mb-1 font-bold">Description</label>
            <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} data-testid="edit-wo-desc"
                      className="w-full border border-[#E2E8F0] dark:border-slate-700 dark:bg-slate-800 dark:text-white rounded-md px-3 py-2 text-sm" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs uppercase tracking-wider text-[#475569] dark:text-slate-400 mb-1 font-bold">Status</label>
              <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} data-testid="edit-wo-status"
                      className="w-full border border-[#E2E8F0] dark:border-slate-700 dark:bg-slate-800 dark:text-white rounded-md px-3 py-2 text-sm">
                <option value="open">Open</option>
                <option value="in_progress">In progress</option>
                <option value="on_hold">On hold</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-[#475569] dark:text-slate-400 mb-1 font-bold">Assigned</label>
              <select value={form.assigned_employee_id} onChange={(e) => setForm({ ...form, assigned_employee_id: e.target.value })} data-testid="edit-wo-employee"
                      className="w-full border border-[#E2E8F0] dark:border-slate-700 dark:bg-slate-800 dark:text-white rounded-md px-3 py-2 text-sm">
                <option value="">— Unassigned —</option>
                {employees.map((e) => <option key={e.user_id} value={e.user_id}>{e.name}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider text-[#475569] dark:text-slate-400 mb-1 font-bold">
              Crew notes <span className="text-amber-600">(visible to assigned employee, NOT to client)</span>
            </label>
            <textarea value={crewNotes} onChange={(e) => setCrewNotes(e.target.value)} rows={2} maxLength={1000}
                      placeholder="e.g. Gate code 5151, dog on site, park on left side"
                      data-testid="edit-wo-crew-notes"
                      className="w-full border border-amber-300 dark:border-amber-700/50 dark:bg-slate-800 dark:text-white rounded-md px-3 py-2 text-sm bg-amber-50/40" />
          </div>
        </div>
        <div className="flex gap-2 justify-end mt-5">
          <button onClick={onClose} className="btn-outline-navy text-sm">Cancel</button>
          <button onClick={save} disabled={saving} className="btn-accent text-sm" data-testid="save-wo">
            {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
      </div>
    </div>
  );
}
